import { HttpClient } from '@adapters/HttpClient';
import { tryResolvePayoutRowCurrency } from '@modules/CODPayments/utils/resolveCurrencyFromApi';
import {
  CreateAdvanceItemsRequest,
  CreateAdvanceItemsResponse,
  CreatePayoutRequest,
  CreatePayoutResponse,
  Payout,
  PayoutFilters,
  PayoutsResponse,
} from '../models';

/** La API a veces omite `currency`; no inventamos EUR aquí (la UI usa moneda del team). */
const mapPayoutCurrency = <T extends Record<string, unknown>>(row: T): Payout => {
  const parsed = tryResolvePayoutRowCurrency(row);
  return {
    ...(row as unknown as Payout),
    ...(parsed !== undefined ? { currency: parsed } : {}),
  };
};

export class PayoutsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene listado de payouts con filtros y paginación
   * GET /api/v1/cod-payouts
   *
   * Query Parameters:
   * - per_page: Registros por página (default: 15)
   * - page: Número de página (default: 1)
   * - sorting[column]: Campo para ordenar
   * - sorting[direction]: asc o desc
   *
   * Advanced Filters:
   * - client_id: equals
   * - payout_date: greater_equal / less_equal
   */
  async getPayouts(filters: PayoutFilters = {}): Promise<PayoutsResponse> {
    const { client_id, date_from, date_to, per_page = 15, page = 1, sort_by, sort_direction } = filters;

    // 🎯 La API exige: si sorting está presente, sorting.column es obligatorio.
    // Solo enviar sorting cuando tenemos AMBOS sort_by y sort_direction válidos.
    const hasValidSorting =
      typeof sort_by === 'string' && sort_by.trim() !== '' && (sort_direction === 'asc' || sort_direction === 'desc');

    const params: Record<string, any> = {
      page,
      per_page,
      ...(hasValidSorting
        ? {
            'sorting[column]': sort_by.trim(),
            'sorting[direction]': sort_direction,
          }
        : {}),
    };

    // 🎯 Construir advanced_filters dinámicamente
    let filterIndex = 0;

    // Filtro por client_id
    if (client_id) {
      params[`advanced_filters[filters][${filterIndex}][column]`] = 'client_id';
      params[`advanced_filters[filters][${filterIndex}][operator]`] = 'equals';
      params[`advanced_filters[filters][${filterIndex}][value]`] = client_id;
      filterIndex++;
    }

    // Filtro por fecha desde (payout_date >= date_from)
    if (date_from) {
      params[`advanced_filters[filters][${filterIndex}][column]`] = 'payout_date';
      params[`advanced_filters[filters][${filterIndex}][operator]`] = 'greater_equal';
      params[`advanced_filters[filters][${filterIndex}][value]`] = date_from;
      filterIndex++;
    }

    // Filtro por fecha hasta (payout_date <= date_to)
    if (date_to) {
      params[`advanced_filters[filters][${filterIndex}][column]`] = 'payout_date';
      params[`advanced_filters[filters][${filterIndex}][operator]`] = 'less_equal';
      params[`advanced_filters[filters][${filterIndex}][value]`] = date_to;
      filterIndex++;
    }

    // Agregar logic si hay filtros
    if (filterIndex > 0) {
      params['advanced_filters[logic]'] = 'AND';
    }

    const response = await this.httpClient.get<PayoutsResponse>('/cod-payouts', params, {}, page === 1);

    return {
      ...response,
      data: (response.data ?? []).map((row) => mapPayoutCurrency(row as Record<string, unknown>)),
    };
  }

  /**
   * Obtiene un payout específico por ID
   * GET /api/v1/cod-payouts/{id}
   */
  async getPayoutById(id: number): Promise<Payout> {
    const response = await this.httpClient.get<Payout>(`/cod-payouts/${id}`);
    return mapPayoutCurrency(response as unknown as Record<string, unknown>);
  }

  /**
   * Crea items de anticipo para pedidos elegibles
   * POST /api/v1/cod-payments/create-advance-items
   */
  async createAdvanceItems(data: CreateAdvanceItemsRequest): Promise<CreateAdvanceItemsResponse> {
    const response = await this.httpClient.post<CreateAdvanceItemsResponse>('/cod-payments/create-advance-items', data);
    return response;
  }

  /**
   * Crea/ejecuta un payout para un cliente
   * POST /api/v1/cod-payouts
   */
  async createPayout(data: CreatePayoutRequest): Promise<CreatePayoutResponse> {
    const response = await this.httpClient.post<CreatePayoutResponse>('/cod-payouts', data);
    return response;
  }

  /**
   * Exporta payouts a Excel
   * POST /api/v1/cod-payouts/export
   */
  async exportPayouts(filters: Partial<PayoutFilters>, columns: string[] = []): Promise<{ url_file: string }> {
    const { client_id, date_from, date_to, sort_by, sort_direction } = filters;

    // Construir query params para sorting (solo si tenemos ambos, la API exige column cuando sorting está presente)
    const queryParams: Record<string, string> = {};
    const hasValidSorting =
      typeof sort_by === 'string' && sort_by.trim() !== '' && (sort_direction === 'asc' || sort_direction === 'desc');
    if (hasValidSorting) {
      queryParams['sorting[column]'] = sort_by!.trim();
      queryParams['sorting[direction]'] = sort_direction!;
    }

    // Construir advanced_filters para el body
    const advancedFilters: Array<{ column: string; operator: string; value: string | number }> = [];

    if (client_id) {
      advancedFilters.push({
        column: 'client_id',
        operator: 'equals',
        value: client_id,
      });
    }

    if (date_from) {
      advancedFilters.push({
        column: 'payout_date',
        operator: 'greater_equal',
        value: date_from,
      });
    }

    if (date_to) {
      advancedFilters.push({
        column: 'payout_date',
        operator: 'less_equal',
        value: date_to,
      });
    }

    // Construir body con columnas y advanced_filters
    const body: { columns: string[]; advanced_filters?: { filters: typeof advancedFilters; logic: 'AND' | 'OR' } } = {
      columns: columns.length > 0 ? columns : ['id', 'total_amount', 'payout_date', 'client.name', 'items_count'],
      ...(advancedFilters.length > 0 && {
        advanced_filters: {
          filters: advancedFilters,
          logic: 'AND',
        },
      }),
    };

    // Construir URL con query params
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `/cod-payouts/export${queryString ? `?${queryString}` : ''}`;

    const response = await this.httpClient.post<{ url_file: string }>(url, body);

    return response;
  }
}
